"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import PageHero from "@/components/page-hero"
import { pageHeroData } from "@/data/page-hero-data"
import { formatPrice } from "@/lib/berlin-time"
import { api } from "@/lib/api"
import {
  Gift,
  Mail,
  CreditCard,
  Sparkles,
  Search,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
} from "lucide-react"

// Online-Kauf via Stripe + E-Mail-Versand.
// Die Seite bietet:
//  • Online-Kaufformular (Wert + Empfänger + persönliche Nachricht)
//  • Restguthaben-Check für bestehende Codes

interface CheckResult {
  code?: string
  discount?: number
  isValueVoucher?: boolean
  remainingValueCents?: number
  initialValueCents?: number
  scope?: "KART" | "POS" | "BOTH"
  discountType?: "PERCENTAGE" | "FIXED"
  message?: string
  ok: boolean
}

const PRESET_VALUES_CENTS = [2500, 5000, 10000, 15000]
const MIN_VALUE_CENTS = 500
const MAX_VALUE_CENTS = 100000

export default function GutscheinPage() {
  const heroData = pageHeroData.gutschein
  const [code, setCode] = useState("")
  const [checking, setChecking] = useState(false)
  const [result, setResult] = useState<CheckResult | null>(null)

  // Kauf-Formular
  const [valueCents, setValueCents] = useState<number>(5000)
  const [customValue, setCustomValue] = useState<string>("")
  const [purchaserEmail, setPurchaserEmail] = useState("")
  const [purchaserName, setPurchaserName] = useState("")
  const [isGift, setIsGift] = useState(false)
  const [recipientEmail, setRecipientEmail] = useState("")
  const [recipientName, setRecipientName] = useState("")
  const [personalMessage, setPersonalMessage] = useState("")
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [paymentProvider, setPaymentProvider] = useState<'STRIPE' | 'PAYPAL'>('STRIPE')

  // ── Abschaltbare Zahlungsmethoden (Admin-Settings) ──
  const [payMethods, setPayMethods] = useState({ stripe: true, paypal: true })
  useEffect(() => {
    api.getPublicBookingSettings()
      .then((s) => setPayMethods({ stripe: s.stripeEnabled ?? true, paypal: s.paypalEnabled ?? true }))
      .catch(() => {})
  }, [])
  // Deaktivierte Auswahl auf aktive Methode korrigieren
  useEffect(() => {
    if (paymentProvider === 'STRIPE' && !payMethods.stripe && payMethods.paypal) setPaymentProvider('PAYPAL')
    if (paymentProvider === 'PAYPAL' && !payMethods.paypal && payMethods.stripe) setPaymentProvider('STRIPE')
  }, [payMethods, paymentProvider])
  const [submitting, setSubmitting] = useState(false)
  const [purchaseError, setPurchaseError] = useState<string | null>(null)

  // Optionale Rechnungsadresse (One-Time-Address auf der Lexoffice-Rechnung)
  const [showInvoiceAddress, setShowInvoiceAddress] = useState(true)
  const [invFirstName, setInvFirstName] = useState("")
  const [invLastName, setInvLastName] = useState("")
  const [invStreet, setInvStreet] = useState("")
  const [invZip, setInvZip] = useState("")
  const [invCity, setInvCity] = useState("")
  const [invCountry, setInvCountry] = useState("DE")

  async function check() {
    if (!code.trim()) return
    setChecking(true)
    setResult(null)
    try {
      const r = await api.validateCoupon(code.trim().toUpperCase(), 0)
      setResult({
        ok: true,
        code: r.code ?? code.trim().toUpperCase(),
        discount: r.discount,
        isValueVoucher: r.isValueVoucher,
        remainingValueCents: r.remainingValueCents,
        initialValueCents: r.initialValueCents,
        scope: r.scope,
        discountType: r.discountType,
      })
    } catch (err) {
      setResult({
        ok: false,
        message: err instanceof Error ? err.message : "Gutschein konnte nicht geprüft werden.",
      })
    } finally {
      setChecking(false)
    }
  }

  function getEffectiveValueCents(): number | null {
    if (customValue.trim()) {
      const parsed = Number(customValue.replace(",", "."))
      if (!Number.isFinite(parsed) || parsed <= 0) return null
      const cents = Math.round(parsed * 100)
      if (cents < MIN_VALUE_CENTS || cents > MAX_VALUE_CENTS) return null
      return cents
    }
    return valueCents
  }

  async function purchase() {
    setPurchaseError(null)
    if (!payMethods.stripe && !payMethods.paypal) {
      setPurchaseError("Online-Zahlung ist derzeit nicht möglich. Bitte kontaktiere uns direkt.")
      return
    }
    const effective = getEffectiveValueCents()
    if (!effective) {
      setPurchaseError(`Bitte einen Wert zwischen ${MIN_VALUE_CENTS / 100} € und ${MAX_VALUE_CENTS / 100} € wählen.`)
      return
    }
    if (!purchaserEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(purchaserEmail.trim())) {
      setPurchaseError("Bitte eine gültige E-Mail-Adresse angeben.")
      return
    }
    if (isGift) {
      if (!recipientEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())) {
        setPurchaseError("Bitte eine gültige Empfänger-E-Mail angeben.")
        return
      }
    }
    if (!acceptTerms) {
      setPurchaseError("Bitte AGB und Datenschutz bestätigen.")
      return
    }
    setSubmitting(true)
    try {
      const res = await api.purchaseVoucher({
        valueCents: effective,
        purchaserEmail: purchaserEmail.trim().toLowerCase(),
        purchaserName: purchaserName.trim() || undefined,
        recipientEmail: isGift ? recipientEmail.trim().toLowerCase() : undefined,
        recipientName: isGift ? recipientName.trim() || undefined : undefined,
        personalMessage: isGift && personalMessage.trim() ? personalMessage.trim() : undefined,
        purchaserFirstName: showInvoiceAddress && invFirstName.trim() ? invFirstName.trim() : undefined,
        purchaserLastName: showInvoiceAddress && invLastName.trim() ? invLastName.trim() : undefined,
        purchaserStreet: showInvoiceAddress && invStreet.trim() ? invStreet.trim() : undefined,
        purchaserZip: showInvoiceAddress && invZip.trim() ? invZip.trim() : undefined,
        purchaserCity: showInvoiceAddress && invCity.trim() ? invCity.trim() : undefined,
        purchaserCountryCode: showInvoiceAddress && invCountry.trim() ? invCountry.trim().toUpperCase() : undefined,
        paymentProvider,
      })
      window.location.href = res.url
    } catch (err) {
      setPurchaseError(err instanceof Error ? err.message : "Kauf konnte nicht gestartet werden.")
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageHero
        title={heroData.title}
        description={heroData.description}
        backgroundImage={heroData.backgroundImage}
      />

      <div className="py-16">
        <div className="container-custom max-w-5xl">
          {/* Intro */}
          <div className="text-center mb-12">
            <Gift className="mx-auto h-12 w-12 text-[var(--main-color)] mb-4" />
            <h2 className="text-3xl font-bold mb-4">Verschenke Rennspaß</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Ob runder Geburtstag, Junggesellenabschied oder einfach so – ein MOBIKART-Gutschein
              passt immer. Wähle einen freien Wert oder ein konkretes Erlebnis.
            </p>
          </div>

          {/* Varianten */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-t-4 border-[var(--main-color)]">
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-orange-100 p-3">
                    <Mail className="h-6 w-6 text-[var(--main-color)]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">E-Mail-Gutschein</h3>
                    <p className="text-sm text-gray-500">Sofort per E-Mail</p>
                  </div>
                </div>
                <p className="text-gray-700">
                  Wähle einen Wert, bezahle online per Stripe und erhalte den Gutschein direkt per
                  E-Mail – optional mit persönlicher Nachricht an einen Beschenkten.
                </p>
                <ul className="text-sm space-y-1.5 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Wert 5&nbsp;€ – 1.000&nbsp;€ frei wählbar
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Restguthaben mehrfach einlösbar
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Gültig für Online-Buchung & vor Ort
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-t-4 border-pink-400">
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-pink-100 p-3">
                    <CreditCard className="h-6 w-6 text-pink-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">PVC-Karte zum Verschenken</h3>
                    <p className="text-sm text-gray-500">Direkt vor Ort erhältlich</p>
                  </div>
                </div>
                <p className="text-gray-700">
                  Edle Plastikkarte mit Rubbelfeld – ideal als greifbares Geschenk. Wert wird beim
                  Kauf an unserer Kasse aktiviert (von 10&nbsp;€ bis 500&nbsp;€ frei wählbar).
                </p>
                <ul className="text-sm space-y-1.5 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Echte Karte mit Seriennummer & Rubbelfeld
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Restguthaben mehrfach einlösbar
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Online & an der Kasse einlösbar
                  </li>
                </ul>
                <Link
                  href="/kontakt"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-pink-500 hover:bg-pink-600 text-white px-4 py-3 font-semibold transition-colors"
                >
                  Vor Ort kaufen <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* Kaufformular */}
          <div id="kaufen" className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6 md:p-8 mb-16">
            <div className="flex items-start gap-3 mb-6">
              <div className="rounded-full bg-orange-100 p-2">
                <Sparkles className="h-5 w-5 text-[var(--main-color)]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Gutschein online kaufen</h3>
                <p className="text-sm text-gray-600">Bezahlung sicher über Stripe – Zustellung per E-Mail.</p>
              </div>
            </div>

            {/* Wert */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Wert</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                {PRESET_VALUES_CENTS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      setValueCents(v)
                      setCustomValue("")
                    }}
                    className={`rounded-lg border-2 px-4 py-3 font-semibold transition-colors ${
                      !customValue && valueCents === v
                        ? "border-[var(--main-color)] bg-orange-50 text-[var(--main-color)]"
                        : "border-gray-200 bg-white text-gray-700 hover:border-orange-300"
                    }`}
                  >
                    {v / 100}&nbsp;€
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={MIN_VALUE_CENTS / 100}
                  max={MAX_VALUE_CENTS / 100}
                  step="1"
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  placeholder={`Eigener Wert (${MIN_VALUE_CENTS / 100} – ${MAX_VALUE_CENTS / 100} €)`}
                  className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                />
                <span className="text-gray-500 text-sm">€</span>
              </div>
            </div>

            {/* Käufer */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Deine E-Mail *</label>
                <input
                  type="email"
                  required
                  value={purchaserEmail}
                  onChange={(e) => setPurchaserEmail(e.target.value)}
                  placeholder="name@beispiel.de"
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Dein Name (optional)</label>
                <input
                  type="text"
                  value={purchaserName}
                  onChange={(e) => setPurchaserName(e.target.value)}
                  placeholder="Max Mustermann"
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                />
              </div>
            </div>

            {/* Geschenk? */}
            <div className="mb-6">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isGift}
                  onChange={(e) => setIsGift(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[var(--main-color)] focus:ring-[var(--main-color)]"
                />
                <span className="text-sm font-semibold text-gray-700">
                  Als Geschenk direkt an Empfänger senden
                </span>
              </label>

              {isGift && (
                <div className="mt-4 space-y-3 rounded-lg border border-orange-200 bg-orange-50 p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Empfänger-E-Mail *
                      </label>
                      <input
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        placeholder="empfaenger@beispiel.de"
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Empfänger-Name (optional)
                      </label>
                      <input
                        type="text"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="Lisa Muster"
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Persönliche Nachricht (optional, max. 300 Zeichen)
                    </label>
                    <textarea
                      value={personalMessage}
                      onChange={(e) => setPersonalMessage(e.target.value.slice(0, 300))}
                      rows={3}
                      placeholder="Alles Gute zum Geburtstag! 🎁"
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)] resize-none"
                    />
                    <p className="text-right text-xs text-gray-500 mt-1">
                      {personalMessage.length}/300
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Optionale Rechnungsadresse */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setShowInvoiceAddress((v) => !v)}
                className="text-sm font-semibold text-[var(--main-color)] hover:underline flex items-center gap-1"
              >
                {showInvoiceAddress ? "− " : "+ "}
                Rechnungsadresse hinzufügen (optional)
              </button>
              {showInvoiceAddress && (
                <div className="mt-3 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs text-gray-600">
                    Trage deine Rechnungsdaten ein, wenn du eine Rechnung mit Anschrift möchtest.
                    Wir senden dir die Rechnung per E-Mail zu.
                    Felder leer lassen → die Rechnung wird ohne Adresse nur an deine E-Mail-Adresse versendet.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Vorname</label>
                      <input
                        type="text"
                        value={invFirstName}
                        onChange={(e) => setInvFirstName(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Nachname</label>
                      <input
                        type="text"
                        value={invLastName}
                        onChange={(e) => setInvLastName(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Straße &amp; Hausnummer</label>
                    <input
                      type="text"
                      value={invStreet}
                      onChange={(e) => setInvStreet(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">PLZ</label>
                      <input
                        type="text"
                        value={invZip}
                        onChange={(e) => setInvZip(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Ort</label>
                      <input
                        type="text"
                        value={invCity}
                        onChange={(e) => setInvCity(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Land (ISO-Code)</label>
                    <input
                      type="text"
                      value={invCountry}
                      onChange={(e) => setInvCountry(e.target.value.toUpperCase().slice(0, 2))}
                      maxLength={2}
                      className="w-32 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Zahlungsart */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Zahlungsart</label>
              <div className="grid grid-cols-2 gap-2">
                {payMethods.stripe && (
                <label
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium cursor-pointer transition-colors ${
                    paymentProvider === 'STRIPE'
                      ? 'border-[var(--main-color)] bg-orange-50 text-[var(--main-color)]'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentProvider"
                    value="STRIPE"
                    checked={paymentProvider === 'STRIPE'}
                    onChange={() => setPaymentProvider('STRIPE')}
                    className="sr-only"
                  />
                  Karte / Apple Pay / Google Pay
                </label>
                )}
                {payMethods.paypal && (
                <label
                  className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium cursor-pointer transition-colors ${
                    paymentProvider === 'PAYPAL'
                      ? 'border-[var(--main-color)] bg-orange-50 text-[var(--main-color)]'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentProvider"
                    value="PAYPAL"
                    checked={paymentProvider === 'PAYPAL'}
                    onChange={() => setPaymentProvider('PAYPAL')}
                    className="sr-only"
                  />
                  PayPal
                </label>
                )}
              </div>
              {!payMethods.stripe && !payMethods.paypal && (
                <p className="mt-2 text-sm text-amber-600">
                  Online-Zahlung ist derzeit nicht möglich. Bitte kontaktiere uns direkt.
                </p>
              )}
            </div>

            {/* AGB */}
            <label className="flex items-start gap-2 cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[var(--main-color)] focus:ring-[var(--main-color)]"
              />
              <span className="text-sm text-gray-700">
                Ich akzeptiere die{" "}
                <Link href="/agb" className="text-[var(--main-color)] hover:underline">AGB</Link>
                {" "}und die{" "}
                <Link href="/datenschutz" className="text-[var(--main-color)] hover:underline">Datenschutzerklärung</Link>.
              </span>
            </label>

            {purchaseError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{purchaseError}</p>
              </div>
            )}

            <button
              type="button"
              onClick={() => void purchase()}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--main-color)] hover:bg-orange-600 text-white px-4 py-3.5 font-bold text-base transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? (
                paymentProvider === 'PAYPAL' ? "Weiterleitung zu PayPal …" : "Weiterleitung zu Stripe …"
              ) : (
                <>
                  <Gift className="h-5 w-5" /> Jetzt kaufen (
                  {((getEffectiveValueCents() ?? 0) / 100).toFixed(2).replace(".", ",")}
                  &nbsp;€)
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
            <p className="text-center text-xs text-gray-500 mt-3">
              {paymentProvider === 'PAYPAL'
                ? 'Sichere Bezahlung via PayPal – PayPal-Konto oder Kreditkarte.'
                : 'Sichere Bezahlung via Stripe – Kredit-/Debitkarte, Apple Pay, Google Pay u. v. m.'}
            </p>
          </div>

          {/* Restguthaben prüfen */}
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 md:p-8 border border-orange-100">
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-full bg-white p-2 shadow-sm">
                <Search className="h-5 w-5 text-[var(--main-color)]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Restguthaben prüfen</h3>
                <p className="text-sm text-gray-600">
                  Gib deinen Gutschein-Code ein, um den aktuellen Stand zu sehen.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void check()
                }}
                placeholder="z. B. ABCD-1234-EFGH"
                className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
              />
              <button
                onClick={() => void check()}
                disabled={!code.trim() || checking}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--main-color)] hover:bg-orange-600 text-white px-6 py-3 font-semibold disabled:opacity-50 transition-colors"
              >
                {checking ? "Prüfe …" : "Prüfen"}
              </button>
            </div>

            {result && !result.ok && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-700">Nicht einlösbar</p>
                  <p className="text-sm text-red-600">{result.message}</p>
                </div>
              </div>
            )}

            {result && result.ok && (
              <div className="rounded-lg border border-green-200 bg-white p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <p className="font-semibold text-green-700">
                    Gutschein <span className="font-mono">{result.code}</span> ist gültig.
                  </p>
                </div>
                {result.isValueVoucher ? (
                  <div className="text-sm text-gray-700 space-y-1 pl-7">
                    <p>
                      Aktuelles Restguthaben:{" "}
                      <span className="text-2xl font-extrabold text-[var(--main-color)]">
                        {formatPrice(result.remainingValueCents ?? 0)}
                      </span>
                    </p>
                    {result.initialValueCents != null && (
                      <p className="text-xs text-gray-500">
                        Ursprungswert: {formatPrice(result.initialValueCents)}
                      </p>
                    )}
                  </div>
                ) : result.discountType === "PERCENTAGE" ? (
                  <p className="pl-7 text-sm text-gray-700">
                    Rabattgutschein – wird beim Buchen automatisch berechnet.
                  </p>
                ) : (
                  <p className="pl-7 text-sm text-gray-700">
                    Festrabatt: <span className="font-semibold">{formatPrice(result.discount ?? 0)}</span>
                  </p>
                )}
                {result.scope && result.scope !== "BOTH" && (
                  <p className="pl-7 text-xs text-gray-500">
                    Einlösbar:{" "}
                    {result.scope === "KART" ? "nur für Kart-Buchungen" : "nur an unserer Kasse"}
                  </p>
                )}
                {result.scope !== "POS" && (
                  <div className="pl-7 pt-2">
                    <Link
                      href="/buchen"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--main-color)] hover:underline"
                    >
                      Jetzt online einlösen <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hinweise */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
            <div className="rounded-lg border bg-white p-4">
              <p className="font-semibold text-gray-800 mb-1">Lange gültig</p>
              <p>Gutscheine sind in der Regel 3 Jahre ab Ausstellung gültig (gem. § 195 BGB).</p>
            </div>
            <div className="rounded-lg border bg-white p-4">
              <p className="font-semibold text-gray-800 mb-1">Teil-Einlösung</p>
              <p>
                Wertgutscheine müssen nicht in einem Zug aufgebraucht werden – Restguthaben bleibt
                für die nächste Buchung erhalten.
              </p>
            </div>
            <div className="rounded-lg border bg-white p-4">
              <p className="font-semibold text-gray-800 mb-1">Übertragbar</p>
              <p>
                Gutscheine sind nicht personalisiert – verschenke sie bedenkenlos weiter.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
